import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import lang from '../../../../lib/language';
import { RootState } from '../../../../lib/store';
import { CloseoutCall } from '../../../../lib/store/closeout/types';
import { getCurrentAccountTradingPlan } from '../../../../lib/store/crm/helpers';
import { CRMCall } from '../../../../lib/store/crm/types';
import { PaymentCall } from '../../../../lib/store/payment/types';
import { isCallStatusPending } from '../../../../lib/util/error-handling/StatusByCallHelpers';

import { Questionmark } from '../../../../components';
import ProgressChart from '../ProgressChart/ProgressChart';
import ProgressPie from '../ProgressPie/ProgressPie';

import { CONSISTENCY_RULE_DANGER, CONSISTENCY_RULE_WARNING, MAX_TRADING_DAYS } from './ScoreCalculator.constant';

import styles from './ScoreCalculator.module.scss';

const ScoreCalculator = (props: ReduxProps) => {
  const {
    tradingPlan,
    isTradingPlanPending,
    isPaymentsPending,
    isAccountCloseoutStatusPending,
    accountStatusData,
  } = props;

  const [isInitialMount, setIsInitialMount] = useState(true);

  const isLoading = isPaymentsPending
    || isTradingPlanPending
    || isAccountCloseoutStatusPending;
  const {
    tradingDays,
    consistencyScore,
    totalRealizedPnl,
    tradeEvent,
    lastTradingDate,
  } = accountStatusData;

  // Check if lastTradingDate is today
  const isLastTradingDateToday = (): boolean => {
    if (!lastTradingDate) return false;
    const dateStr = (lastTradingDate instanceof Date ? lastTradingDate.toISOString() : lastTradingDate).split('T')[0];
    return dateStr === new Date().toISOString().split('T')[0];
  };

  // Calculate display trading days: increment by 1 if tradeEvent exists and lastTradingDate is not today
  const displayTradingDays = (tradeEvent && !isLastTradingDateToday())
    ? tradingDays + 1
    : tradingDays;

  const hasValidData = consistencyScore !== undefined
  && consistencyScore !== null
  && tradingDays !== undefined
  && tradingDays !== null;

  useEffect(() => {
    if (hasValidData && !isLoading) {
      setIsInitialMount(false);
    }
  }, [hasValidData, isLoading]);

  const showLoading = isLoading || !hasValidData || isInitialMount;
  // Use dynamic consistency coefficient from trading plan, default to 70
  const consistencyRuleWarning = tradingPlan?.consistency_coefficient ?? CONSISTENCY_RULE_WARNING;
  // Use dynamic consistency coefficient minimum from trading plan, default to 50
  const consistencyRuleDanger = tradingPlan?.consistency_coefficient_minimum ?? CONSISTENCY_RULE_DANGER;
  const getConsistencyRuleColor = (): string => {
    if (consistencyScore < consistencyRuleDanger) {
      return '#F96C75';
    }
    if (consistencyScore < consistencyRuleWarning) {
      return '#F6C98E';
    }
    return '#1BFEBA';
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.pieChart} id={'scoreCalculatorPieChart'}>
          <ProgressPie
            isLoading={isLoading}
            progress={totalRealizedPnl}
            maxValue={tradingPlan?.profit_target_amount ?? 0}
            color={'#1BFEBA'}
            size={218}
          />
        </div>
        <div className={styles.componentContainer}>
          <div className={styles.header}>
            <div className={styles.titles}>
              <div className={styles.title}>{lang.consistencyRule()}</div>
              <Questionmark
                title={lang.consistencyRule()}
                text={lang.consistencyRuleDescription()}
                boldText={lang.consistencyRuleScoreOfAtLeast75(consistencyRuleWarning)}
              />
            </div>
            {consistencyScore !== undefined && consistencyScore !== null ? (
              <div className={styles.percentage}>
                <div className={classNames(styles.dot, {
                  [styles.red]: consistencyScore < consistencyRuleDanger,
                  [styles.orange]: consistencyScore > consistencyRuleDanger
                && consistencyScore < consistencyRuleWarning,
                  [styles.green]: consistencyScore >= consistencyRuleWarning,
                })}
                />
                {`${consistencyScore.toFixed(0)}%`}
              </div>
            ) : <div className={styles.textLoader} />}
          </div>
          <div className={styles.bars}>
            <div className={styles.bigBar}>
              <ProgressChart
                value={showLoading ? 0 : consistencyScore < consistencyRuleDanger // eslint-disable-line
                  ? consistencyScore : consistencyRuleDanger}
                maxValue={consistencyRuleDanger}
                height={6}
                color={getConsistencyRuleColor()}
              />
            </div>
            <div
              style={{ width: `${consistencyRuleWarning - consistencyRuleDanger}%` }}
            >
              <ProgressChart
                value={showLoading ? 0 : consistencyScore > consistencyRuleDanger // eslint-disable-line
                  ? consistencyScore - consistencyRuleDanger
                  < (consistencyRuleWarning - consistencyRuleDanger)
                    ? consistencyScore - consistencyRuleDanger
                    : (consistencyRuleWarning - consistencyRuleDanger) : 0}
                maxValue={consistencyRuleWarning - consistencyRuleDanger}
                height={6}
                color={getConsistencyRuleColor()}
              />
            </div>
            <div
              style={{ width: `${100 - consistencyRuleWarning}%` }}
            >
              <ProgressChart
                value={showLoading ? 0 : consistencyScore > consistencyRuleWarning // eslint-disable-line
                  ? Math.min(consistencyScore - consistencyRuleWarning, 100 - consistencyRuleWarning)
                  : 0}
                maxValue={100 - consistencyRuleWarning}
                height={6}
                color={getConsistencyRuleColor()}
              />
            </div>
          </div>
        </div>
        <div className={styles.componentContainer}>
          <div className={styles.header}>
            <div className={styles.titles}>
              <div className={styles.title}>{lang.tradingDays()}</div>
              <Questionmark text={lang.tradingDaysDescription()} title={lang.tradingDays()} />
            </div>
            {tradingDays !== undefined && tradingDays !== null ? (
              <div className={styles.percentage}>
                {`${displayTradingDays}/${MAX_TRADING_DAYS}`}
              </div>
            ) : <div className={styles.textLoader} />}
          </div>
          <div className={styles.bars}>
            {Array.from({ length: MAX_TRADING_DAYS }).map((_, index) => (
              <div key={`${index.toString()}`} className={styles.bar}>
                <ProgressChart
                  value={showLoading ? 0 : index < displayTradingDays ? 1 : 0} // eslint-disable-line
                  maxValue={1}
                  height={6}
                  color={index < displayTradingDays ? '#1BFEBA' : '#3BB0FF33'}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  tradingPlan: getCurrentAccountTradingPlan(state.crm),
  accountStatusData: state.closeout.accountStatusData,
  isPaymentsPending: isCallStatusPending(PaymentCall.getPaymentsAll, state.payment.statusByCall),
  isTradingPlanPending: isCallStatusPending(CRMCall.searchTradingPlans, state.crm.statusByCall),
  isAccountCloseoutStatusPending: isCallStatusPending(
    CloseoutCall.getAccountCloseoutStatus, state.closeout.statusByCall,
  ),
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ScoreCalculator);
